import { useState, useEffect, useMemo } from 'react'
import { FiX, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import api from '../utils/api'

// Market tenant firmalarinin (orn. Aksantas) cari detay penceresi: ozet + son islemler + yil/ay hareketleri.
// Veri: GET /api/ai-analiz/cari-hareket?musteri=... (backend services/firma_bakiye.cari_hareket)
// Widget config `cari_detay_modal: true` olan tablolarda UNVAN'li satira tiklayinca acilir (ChartWidget).

const fmtTL = (v) => (v == null ? '—' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺')
const fmtNum = (v) => Number(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })
const fmtTarih = (s) => (s ? s.split('-').reverse().join('.') : '—')
const AY_AD = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']
const SON_ISLEM_ADEDI = 30

function Kart({ etiket, deger, renk, alt }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{etiket}</div>
      <div className={`text-sm font-bold mt-0.5 ${renk || 'text-gray-800 dark:text-gray-100'}`}>{deger}</div>
      {alt && <div className="text-[10px] text-gray-500 dark:text-gray-400">{alt}</div>}
    </div>
  )
}

export default function FirmaCariModal({ musteri, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sekme, setSekme] = useState('son')
  const [acikSatir, setAcikSatir] = useState({})
  const [openYil, setOpenYil] = useState({})
  const [openAy, setOpenAy] = useState({})

  useEffect(() => {
    if (!musteri) return
    setLoading(true)
    api.get('/ai-analiz/cari-hareket', { params: { musteri } })
      .then(({ data }) => {
        setData(data)
        if (data.yillar && data.yillar[0]) setOpenYil({ [data.yillar[0].yil]: true })
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false))
  }, [musteri])

  // Tum hareketler (eskiden yeniye) + islem sonrasi bakiye; son islemler en yeni ustte
  const { sonIslemler, sonSatis, sonOdeme } = useMemo(() => {
    const tum = []
    for (const y of data?.yillar || []) for (const a of y.aylar) for (const h of a.hareketler) tum.push(h)
    tum.sort((x, y) => (x.tarih + (x.saat || '')).localeCompare(y.tarih + (y.saat || '')))
    let bakiye = 0
    const zincir = tum.map(h => { bakiye += (h.borc || 0) - (h.alacak || 0); return { ...h, bakiye } })
    const satis = [...zincir].reverse().find(h => h.borc > 0)
    const odeme = [...zincir].reverse().find(h => h.alacak > 0)
    return { sonIslemler: zincir.slice(-SON_ISLEM_ADEDI).reverse(), sonSatis: satis, sonOdeme: odeme }
  }, [data])

  if (!musteri) return null
  const net = data?.net_bakiye || 0
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-50 dark:bg-gray-900 rounded-xl shadow-2xl max-w-5xl w-full flex flex-col overflow-hidden"
        style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 truncate">{musteri}</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Cari detay · {data?.toplam_hareket ?? '…'} hareket</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1"><FiX size={20} /></button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Hareketler yükleniyor...</div>
        ) : !data || !data.yillar?.length ? (
          <div className="p-10 text-center text-sm text-gray-500">Hareket bulunamadı</div>
        ) : (
          <>
            <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-5 gap-2">
              <Kart etiket="Toplam Borç (Satış)" deger={fmtTL(data.toplam_borc)} renk="text-red-600" />
              <Kart etiket="Toplam Ödeme (Alacak)" deger={fmtTL(data.toplam_alacak)} renk="text-green-600" />
              <Kart etiket="Bakiye" deger={fmtTL(Math.abs(net))}
                renk={net > 0.005 ? 'text-red-600' : 'text-green-600'}
                alt={net > 0.005 ? 'Müşteri bize borçlu' : net < -0.005 ? 'Biz borçluyuz / fazla ödeme' : 'Hesap kapalı'} />
              <Kart etiket="Son Alışveriş" deger={fmtTarih(sonSatis?.tarih)} alt={sonSatis ? fmtTL(sonSatis.borc) : ''} />
              <Kart etiket="Son Ödeme" deger={fmtTarih(sonOdeme?.tarih)} alt={sonOdeme ? `${fmtTL(sonOdeme.alacak)} · ${sonOdeme.sube}` : ''} />
            </div>

            <div className="px-5 flex gap-1 border-b border-gray-200 dark:border-gray-700">
              {[['son', `Son ${SON_ISLEM_ADEDI} İşlem`], ['tum', 'Tüm Hareketler (Yıl / Ay)']].map(([k, l]) => (
                <button key={k} onClick={() => setSekme(k)}
                  className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${sekme === k ? 'border-primary-600 text-primary-700 dark:text-primary-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  {l}
                </button>
              ))}
            </div>

            <div className="overflow-auto flex-1 bg-white dark:bg-gray-800">
              {sekme === 'son' ? (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                    <tr className="text-gray-600 dark:text-gray-300">
                      <th className="px-3 py-2 text-left">Tarih</th>
                      <th className="px-3 py-2 text-left">İşlem</th>
                      <th className="px-3 py-2 text-left">Açıklama</th>
                      <th className="px-3 py-2 text-left">Belge No</th>
                      <th className="px-3 py-2 text-right">Borç</th>
                      <th className="px-3 py-2 text-right">Ödeme</th>
                      <th className="px-3 py-2 text-right">Bakiye</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sonIslemler.map((h, i) => {
                      const urunler = h.gun_urunleri
                      const acik = acikSatir[i]
                      return [
                        <tr key={i} onClick={urunler ? () => setAcikSatir(p => ({ ...p, [i]: !p[i] })) : undefined}
                          className={`border-b border-gray-100 dark:border-gray-700/40 ${urunler ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40' : ''}`}>
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtTarih(h.tarih)} <span className="text-gray-400">{h.saat}</span></td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${h.borc > 0 ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'}`}>
                              {urunler && (acik ? <FiChevronDown size={10} /> : <FiChevronRight size={10} />)}{h.sube}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-gray-600 dark:text-gray-300 truncate" style={{ maxWidth: 260 }} title={h.aciklama}>{h.aciklama || '—'}</td>
                          <td className="px-3 py-1.5 text-gray-500">{h.fatura_no || '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-red-600">{h.borc > 0 ? fmtTL(h.borc) : ''}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-green-600">{h.alacak > 0 ? fmtTL(h.alacak) : ''}</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${h.bakiye > 0.005 ? 'text-red-600' : 'text-green-600'}`}>{fmtTL(Math.abs(h.bakiye))}</td>
                        </tr>,
                        acik && urunler && (
                          <tr key={`${i}-u`} className="bg-gray-50 dark:bg-gray-900/30">
                            <td colSpan={7} className="px-8 py-2">
                              <div className="text-[10px] text-gray-500 mb-1">{fmtTarih(h.tarih)} günü alınan ürünler</div>
                              <table className="text-[11px]">
                                <tbody>
                                  {urunler.map((u, j) => (
                                    <tr key={j}>
                                      <td className="pr-6 py-0.5 text-gray-700 dark:text-gray-200">{u.urun}</td>
                                      <td className="pr-6 py-0.5 text-right tabular-nums text-gray-500">{fmtNum(u.miktar)} {u.birim}</td>
                                      <td className="py-0.5 text-right tabular-nums">{fmtTL(u.tutar)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        ),
                      ]
                    })}
                  </tbody>
                </table>
              ) : (
                data.yillar.map(y => (
                  <div key={y.yil} className="border-b border-gray-100 dark:border-gray-700">
                    <button onClick={() => setOpenYil(p => ({ ...p, [y.yil]: !p[y.yil] }))}
                      className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm">
                      <div className="flex items-center gap-2">
                        {openYil[y.yil] ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                        <span className="font-semibold">{y.yil}</span>
                      </div>
                      <div className="flex gap-3 text-[11px]">
                        <span className="text-red-600">Borç: {fmtTL(y.borc)}</span>
                        <span className="text-green-600">Ödeme: {fmtTL(y.alacak)}</span>
                      </div>
                    </button>
                    {openYil[y.yil] && y.aylar.map(a => {
                      const ayKey = `${y.yil}-${a.ay}`
                      return (
                        <div key={a.ay} className="ml-4 border-l border-gray-200 dark:border-gray-700">
                          <button onClick={() => setOpenAy(p => ({ ...p, [ayKey]: !p[ayKey] }))}
                            className="w-full px-3 py-1.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 text-xs">
                            <div className="flex items-center gap-2">
                              {openAy[ayKey] ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                              <span>{AY_AD[a.ay]} ({a.hareketler.length})</span>
                            </div>
                            <div className="flex gap-2 text-[10px]">
                              <span className="text-red-600">B: {fmtTL(a.borc)}</span>
                              <span className="text-green-600">Ö: {fmtTL(a.alacak)}</span>
                            </div>
                          </button>
                          {openAy[ayKey] && (
                            <table className="w-full text-[11px]">
                              <tbody>
                                {a.hareketler.map((h, i) => (
                                  <tr key={i} className="border-b border-gray-50 dark:border-gray-700/30">
                                    <td className="px-3 py-1 whitespace-nowrap">{fmtTarih(h.tarih)}</td>
                                    <td className="px-2 py-1 text-gray-600">{h.sube}</td>
                                    <td className="px-2 py-1 truncate" style={{ maxWidth: 280 }} title={h.aciklama}>{h.aciklama || '—'}</td>
                                    <td className="px-2 py-1 text-gray-500">{h.fatura_no || ''}</td>
                                    <td className="px-2 py-1 text-right tabular-nums text-red-600">{h.borc > 0 ? fmtTL(h.borc) : ''}</td>
                                    <td className="px-3 py-1 text-right tabular-nums text-green-600">{h.alacak > 0 ? fmtTL(h.alacak) : ''}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
